/**
 * Facts about the shape of a transaction's fields, shared by forms, validators
 * and render helpers.
 *
 * Deliberately dependency-free so pure validators (e.g.
 * `src/locations/banking/validation.jsx`) can import it without pulling in MUI
 * via `general_util.jsx`. Re-exported from `general_util` for existing callers.
 */

// Display-only stand-in shown wherever a transaction line carries no contact.
// It is NOT a contact name and NOT a value: never seed it into a form field,
// never let it satisfy a validation check, never persist it as a contact.
export const NO_CONTACT_PLACEHOLDER = "Journal Entry";

/**
 * True when a form's contact value carries no real contact.
 *
 * A transaction line may legitimately have no contact — journal entries,
 * donation batches, Pushpay sweeps and imports are all created with
 * `line.contact === null`. Screens render those as "Journal Entry" so the
 * column is not blank, but that string is a *label*, not a value, and it has
 * historically leaked out of render helpers into form state where it looked
 * like a filled field and would have been saved as a new contact by that name.
 *
 * Accepts a contact id, a free-typed contact name, null, undefined or "".
 *
 * @param {string|null|undefined} value
 * @returns {boolean}
 */
export const isBlankContact = (value) =>
  value === null ||
  value === undefined ||
  (typeof value === "string" &&
    (value.trim() === "" || value.trim() === NO_CONTACT_PLACEHOLDER));

/**
 * True when a stored transaction is a journal entry.
 *
 * The `journalEntry` field is written as `true` by the JE form
 * (enter_transaction.jsx:219/286) but the activity tracker then REPLACES it
 * with a sequential JE number — see functions/transactions/activity_tracker.js
 * :192-211, which queries `where("journalEntry", "!=", false)` and writes
 * `journalEntry: <number>`. So a persisted journal entry almost never holds
 * `true`; it holds 1, 2, 3, … Bank transactions hold `false`.
 *
 * Checking `=== true` therefore misses essentially every saved journal entry.
 * Accept both forms, and treat missing/false/0 as "not a journal entry" so
 * legacy docs without the field are not silently reclassified.
 *
 * @param {{journalEntry?: boolean|number}} transaction
 * @returns {boolean}
 */
export const isJournalEntryTransaction = (transaction) =>
  transaction?.journalEntry === true || Number(transaction?.journalEntry) > 0;
